import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardService } from '../../services/dashboardService';
import { AlertTriangle, ArrowRight, Snowflake, Clock, DollarSign } from 'lucide-react';

const AccountsAttentionWidget = () => {
  const [accounts, setAccounts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAccountsNeedingAttention();
  }, []);

  const fetchAccountsNeedingAttention = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await dashboardService.getAccountsNeedingAttention(5);
      setAccounts(response.data.accounts || []);
      setTotalCount(response.data.total_count || 0);
    } catch (error) {
      console.error('Error fetching accounts needing attention:', error);
      setError('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value);
  };

  const getAttentionIcon = (reason) => {
    switch (reason) {
      case 'frozen':
        return <Snowflake className="h-4 w-4 text-blue-600" />;
      case 'low_balance':
        return <DollarSign className="h-4 w-4 text-yellow-600" />;
      case 'inactive':
        return <Clock className="h-4 w-4 text-gray-600" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-red-600" />;
    }
  };

  const getAttentionStyles = (reason) => {
    switch (reason) {
      case 'frozen':
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          text: 'text-blue-800',
          badge: 'bg-blue-100 text-blue-800'
        };
      case 'low_balance':
        return {
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          text: 'text-yellow-800',
          badge: 'bg-yellow-100 text-yellow-800'
        };
      case 'inactive':
        return {
          bg: 'bg-gray-50',
          border: 'border-gray-200',
          text: 'text-gray-800',
          badge: 'bg-gray-100 text-gray-800'
        };
      default:
        return {
          bg: 'bg-red-50',
          border: 'border-red-200',
          text: 'text-red-800',
          badge: 'bg-red-100 text-red-800'
        };
    }
  };

  const getAttentionLabel = (reason) => {
    switch (reason) {
      case 'frozen':
        return 'Frozen';
      case 'low_balance':
        return 'Low Balance';
      case 'inactive':
        return 'Inactive';
      default:
        return 'Attention';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="flex items-center justify-between mb-4">
            <div className="h-6 w-40 bg-gray-200 rounded"></div>
          </div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-gray-100 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <AlertTriangle className="mr-2 h-5 w-5 text-red-500" />
            Accounts Needing Attention
          </h3>
          <button
            onClick={fetchAccountsNeedingAttention}
            className="text-yellow-600 hover:text-gray-800 text-sm"
          >
            Retry
          </button>
        </div>
        <div className="text-center py-4">
          <AlertTriangle className="mx-auto h-8 w-8 text-red-400 mb-2" />
          <p className="text-sm text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <AlertTriangle className="mr-2 h-5 w-5 text-red-500" />
            Needs Attention
            {totalCount > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                {totalCount}
              </span>
            )}
          </h3>
          {totalCount > 5 && (
            <Link
              to="/trust-accounts?filter=attention"
              className="text-yellow-600 hover:text-gray-800 flex items-center text-sm font-medium"
            >
              View All
              <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          )}
        </div>
      </div>

      {/* Account List */}
      <div className="p-4">
        {accounts.length === 0 ? (
          <div className="text-center py-6">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-3">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-sm text-gray-500">All accounts are in good standing</p>
          </div>
        ) : (
          <div className="space-y-3">
            {accounts.map((account) => {
              const styles = getAttentionStyles(account.attention_reason);
              return (
                <Link
                  key={account.id}
                  to={`/trust-accounts/${account.id}`}
                  className={`block p-3 rounded-lg border ${styles.bg} ${styles.border} hover:shadow-md transition-shadow`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <div className="mt-0.5">
                        {getAttentionIcon(account.attention_reason)}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {account.client_name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {account.account_number}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-bold ${styles.text}`}>
                        {formatCurrency(account.current_balance)}
                      </p>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles.badge}`}>
                        {getAttentionLabel(account.attention_reason)}
                      </span>
                    </div>
                  </div>
                  {account.days_inactive && account.attention_reason === 'inactive' && (
                    <p className="mt-2 text-xs text-gray-500">
                      No activity for {account.days_inactive} days
                    </p>
                  )}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountsAttentionWidget;
